import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Mail, MapPin, MessageCircle } from "lucide-react";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Contact() {
  const { t } = useLang();
  return (
    <section id="contact" className="relative overflow-hidden bg-[#141210] pt-28 pb-10" data-testid="contact-section">
      <div className="absolute inset-0 blueprint-lines opacity-50" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-brass/40 bg-brass/10 px-4 py-2 text-xs font-bold text-brasslight">
            <span className="w-1.5 h-1.5 rounded-full bg-brass" />
            {t.contactEyebrow}
          </span>
          <h2 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            {t.contactPre}{" "}
            <em className="font-serif italic font-medium shimmer-text">{t.contactAccent}</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-cream/65 leading-relaxed">{t.contactBody}</p>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-order-whatsapp-button"
            className="pulse-ring mt-9 inline-flex items-center gap-2.5 rounded-full bg-brass px-9 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-95"
          >
            <MessageCircle size={19} strokeWidth={2.4} />
            {t.contactCta} <ArrowUpRight size={17} strokeWidth={2.5} className="rtl:-scale-x-100" />
          </a>
          <p className="mt-6 text-sm font-semibold text-cream/70">
            <a href={PHONE_TEL} data-testid="contact-phone-link" className="hover:text-brasslight transition-colors">
              {PHONE_DISPLAY}
            </a>
            <span className="mx-2 text-taupe">·</span>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="hover:text-brasslight transition-colors">
              {EMAIL}
            </a>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          <div className="rounded-3xl bg-espresso/80 border border-cream/5 p-7">
            <MapPin size={20} className="text-brass" />
            <h3 className="mt-4 font-display text-sm font-bold uppercase text-taupe">{t.contactVisit}</h3>
            <p className="mt-2 text-sm leading-relaxed text-cream/80">{t.contactAddress}</p>
          </div>
          <div className="rounded-3xl bg-espresso/80 border border-cream/5 p-7">
            <Clock size={20} className="text-brass" />
            <h3 className="mt-4 font-display text-sm font-bold uppercase text-taupe">{t.contactHours}</h3>
            <p className="mt-2 text-sm leading-relaxed text-cream/80">{t.contactHoursValue}</p>
          </div>
          <div className="rounded-3xl bg-espresso/80 border border-cream/5 p-7">
            <Mail size={20} className="text-brass" />
            <h3 className="mt-4 font-display text-sm font-bold uppercase text-taupe">{t.contactWrite}</h3>
            <p className="mt-2 text-sm leading-relaxed text-cream/80" dir="ltr">
              {EMAIL}
              <br />
              {PHONE_DISPLAY}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          className="mt-5 overflow-hidden rounded-3xl border border-cream/5"
          data-testid="contact-map"
        >
          <iframe
            title="Ghauri Traders location — Jinnah Garden, Islamabad"
            src="https://maps.google.com/maps?q=Jinnah%20Garden%20Islamabad%20Pakistan&t=&z=13&ie=UTF8&iwloc=&output=embed"
            className="h-64 w-full grayscale invert-[0.92] contrast-[0.85] opacity-80"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>

        <div className="mt-16 border-t border-cream/10 pt-7 text-xs text-taupe">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p>{t.footer1}</p>
            <p className="font-semibold text-cream/50">{t.footer2}</p>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {t.seoLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`footer-link-${l.href.slice(1)}`}
                className="font-semibold text-taupe hover:text-brasslight transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
